/**
 * Mg Theme — Pub/Sub Event System
 * © 2026 NAMY SOLUTIONS LLC
 *
 * Centralized event bus for cross-component communication.
 * Usage:
 *   MgEvents.subscribe('cart:updated', (data) => { ... });
 *   MgEvents.publish('cart:updated', { items: 3 });
 *   MgEvents.unsubscribe('cart:updated', handler);
 */

window.MgEvents = window.MgEvents || (() => {
  const listeners = {};

  function subscribe(event, callback) {
    if (!listeners[event]) {
      listeners[event] = [];
    }
    listeners[event].push(callback);
  }

  function unsubscribe(event, callback) {
    if (!listeners[event]) return;
    listeners[event] = listeners[event].filter((cb) => cb !== callback);
  }

  function publish(event, data) {
    if (!listeners[event]) return;
    listeners[event].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        if (window.Mg) Mg.debug('Events:' + event, error);
      }
    });
  }

  /**
   * Single source of truth for event names. Always reference via
   * MgEvents.Events.* instead of string literals — typos caught by
   * minifier + refactoring safety across PostHog/GA4/Meta Pixel consumers.
   */
  const Events = Object.freeze({
    /* Product discovery + interaction */
    PRODUCT_VIEWED: 'product:viewed',
    PRODUCT_VARIANT_CHANGED: 'product:variant:changed',
    TIER_SELECTED: 'tier:selected',
    SUBSCRIBE_MODE_CHANGED: 'subscribe:mode:changed',
    QUICKADD_OPENED: 'quickadd:opened',
    /* Cart funnel */
    ATC_CLICKED: 'atc:clicked',
    BACKINSTOCK_SIGNUP: 'backinstock:signup',
    CART_UPDATED: 'cart:updated',
    CART_ITEM_ADDED: 'cart:item:added',
    CART_DRAWER_TOGGLE: 'cart:drawer:toggle',
    /* Wishlist */
    WISHLIST_UPDATED: 'wishlist:updated',
    CROSS_SELL_TOGGLE_UPDATED: 'cross-sell:toggle:updated',
    /* Header */
    /* Un panneau d'onglets qui devient visible. Un composant monte pendant que
       son hote est `hidden` mesure zero et ne s'en remet pas : Splide fige une
       piste plate (mesure : panneau 1304x104, piste 1304x0, diapositive 0x0, le
       contenu present et invisible). Le revele publie, le monte ecoute. */
    PANEL_REVEALED: 'panel:revealed',

    HEADER_DRAWER_OPEN: 'header:drawer:open',
    HEADER_DRAWER_CLOSE: 'header:drawer:close',
    HEADER_DRAWER_OPENED: 'header:drawer:opened',
    HEADER_DRAWER_CLOSED: 'header:drawer:closed',
    /* Quiz funnel */
    QUIZ_STARTED: 'quiz:started',
    QUIZ_STEP_VIEWED: 'quiz:step:viewed',
    QUIZ_ANSWER_SELECTED: 'quiz:answer:selected',
    QUIZ_COMPLETED: 'quiz:completed',
    QUIZ_RESULT_VIEWED: 'quiz:result:viewed',
    QUIZ_RESULT_ATC: 'quiz:result:atc',
    QUIZ_EMAIL_CAPTURED: 'quiz:email:captured',
    QUIZ_ABANDONED: 'quiz:abandoned'
  });

  /**
   * track(eventName, payload) — analytics fan-out.
   * Forwards the event to any analytics SDK the merchant has loaded:
   *   - window.dataLayer.push (GA4 / GTM)
   *   - window.fbq (Meta Pixel)
   *   - window.posthog.capture (PostHog)
   * All calls are guarded — zero error when no analytics present.
   *
   * Use for conversion events that matter for reporting. Internal UI
   * sync uses publish() only.
   */
  function track(eventName, payload) {
    const data = payload || {};
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, data));
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, data);
      }
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(eventName, data);
      }
    } catch (error) {
      if (window.Mg) Mg.debug('Track:' + eventName, error);
    }
  }

  return { subscribe, unsubscribe, publish, track, Events };
})();
